import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Box, Tabs, Tab, Typography, Paper, Button,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert
} from '@mui/material';

// Helper to format Firestore Timestamp
const formatDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString('id-ID');
  }
  return typeof timestamp === 'string' ? timestamp : 'N/A';
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const PersetujuanPengajuanDosen = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  
  // State for each type of request
  const [bahanRequests, setBahanRequests] = useState([]);
  const [alatRequests, setAlatRequests] = useState([]);
  const [instrumenRequests, setInstrumenRequests] = useState([]);

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setError('Silakan login.');
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch requests
  const fetchRequests = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError('');
    try {
      // Fetch bahan requests
      const bahanRef = collection(db, 'permintaan_bahan');
      const bahanQuery = query(
        bahanRef,
        where('status', '==', 'pending'),
        where('dosenTerkaitId', '==', currentUser.uid)
      );
      const bahanSnapshot = await getDocs(bahanQuery);
      const bahanData = bahanSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBahanRequests(bahanData);

      // Fetch alat requests
      const alatRef = collection(db, 'permintaan_alat');
      const alatQuery = query(
        alatRef,
        where('status', '==', 'pending'),
        where('dosenTerkaitId', '==', currentUser.uid),
        where('jenisAlat', '==', 'alat_gelas')
      );
      const alatSnapshot = await getDocs(alatQuery);
      const alatData = alatSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAlatRequests(alatData);

      // Fetch instrumen requests
      const instrumenQuery = query(
        alatRef,
        where('status', '==', 'pending'),
        where('dosenTerkaitId', '==', currentUser.uid),
        where('jenisAlat', '==', 'instrumen')
      );
      const instrumenSnapshot = await getDocs(instrumenQuery);
      const instrumenData = instrumenSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInstrumenRequests(instrumenData);

    } catch (err) {
      console.error("Error fetching requests:", err);
      setError('Gagal memuat data permintaan.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchRequests();
    }
  }, [currentUser]);

  const handleUpdateStatus = async (id, collection, newStatus) => {
    setUpdatingId(id);
    setError('');
    try {
      const requestRef = doc(db, collection, id);
      const updateData = {
        status: newStatus === 'approved' ? 'dosen_approved' : 'dosen_rejected',
        tanggalPersetujuanDosen: Timestamp.now(),
        approvalHistory: [{
          status: newStatus === 'approved' ? 'dosen_approved' : 'dosen_rejected',
          timestamp: Timestamp.now(),
          approver: {
            uid: currentUser.uid,
            role: 'dosen'
          }
        }]
      };

      await updateDoc(requestRef, updateData);
      await fetchRequests();
    } catch (err) {
      console.error(`Error updating request ${id}:`, err);
      setError(`Gagal memperbarui status permintaan: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  const renderTable = (requests, collection) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nama Mahasiswa</TableCell>
            <TableCell>NIM</TableCell>
            <TableCell>Nama Item</TableCell>
            <TableCell>Jumlah</TableCell>
            <TableCell>Tujuan</TableCell>
            <TableCell>Tanggal Pengajuan</TableCell>
            <TableCell>Aksi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((req) => (
            <TableRow key={req.id}>
              <TableCell>{req.namaUser}</TableCell>
              <TableCell>{req.userNim}</TableCell>
              <TableCell>{req.namaBahan || req.namaAlat}</TableCell>
              <TableCell>{req.jumlahDiminta || req.jumlah} {req.satuan}</TableCell>
              <TableCell>{req.tujuanPenggunaan}</TableCell>
              <TableCell>{formatDate(req.tanggalPermintaan)}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleUpdateStatus(req.id, collection, 'approved')}
                    disabled={updatingId === req.id}
                  >
                    {updatingId === req.id ? '...' : 'Setujui'}
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleUpdateStatus(req.id, collection, 'rejected')}
                    disabled={updatingId === req.id}
                  >
                    {updatingId === req.id ? '...' : 'Tolak'}
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {requests.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                Tidak ada permintaan yang menunggu persetujuan
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading && !bahanRequests.length && !alatRequests.length && !instrumenRequests.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Memuat data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Persetujuan Pengajuan
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Bahan" />
          <Tab label="Alat Gelas" />
          <Tab label="Instrumen" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Typography variant="h6" gutterBottom>Permintaan Bahan</Typography>
        {renderTable(bahanRequests, 'permintaan_bahan')}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom>Permintaan Alat Gelas</Typography>
        {renderTable(alatRequests, 'permintaan_alat')}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>Permintaan Instrumen</Typography>
        {renderTable(instrumenRequests, 'permintaan_alat')}
      </TabPanel>
    </Box>
  );
};

export default PersetujuanPengajuanDosen;
